import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Plus, Search, MapPin, PlayCircle, PackageOpen } from 'lucide-react';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { formatMoney } from '../lib/money';
import api from '../services/api';

const CATEGORIES = ['all', 'Electronics', 'Furniture', 'Clothing', 'Home & Garden', 'Sports', 'Toys', 'Vehicles', 'Other'];

export default function Marketplace() {
  const nav = useNavigate();
  const { delivery } = useApp();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');

  useEffect(() => {
    setLoading(true);
    const params = { category: cat };
    if (delivery?.lat != null && delivery?.lng != null) { params.lat = delivery.lat; params.lng = delivery.lng; }
    api.get('/marketplace/listings', { params })
      .then((r) => setListings(r.data || []))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [cat, delivery?.lat, delivery?.lng]);

  const filtered = useMemo(() => {
    if (!q.trim()) return listings;
    const ql = q.toLowerCase();
    return listings.filter((l) => l.title.toLowerCase().includes(ql) || (l.description || '').toLowerCase().includes(ql));
  }, [q, listings]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header showSearch={false} />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <ShoppingBag className="h-6 w-6 text-emerald-600" /> Marketplace
            </h1>
            <p className="text-slate-500 mt-1 text-sm">
              {delivery?.address
                ? <>Items near <b className="text-slate-700">{delivery.address}</b> — nearest first. ShipitNow drivers deliver.</>
                : 'Buy & sell locally — set your delivery address to see the closest items first.'}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/marketplace/mine">
              <Button variant="outline" data-testid="my-listings-btn">My listings</Button>
            </Link>
            <Button onClick={() => nav('/marketplace/new')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sell-item-btn">
              <Plus className="h-4 w-4 mr-1" /> Sell an item
            </Button>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="pl-9 h-10" data-testid="marketplace-search" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${cat === c ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                data-testid={`marketplace-cat-${c.toLowerCase().replace(/[^a-z]/g, '')}`}>
                {c === 'all' ? 'All' : c}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-12 text-center text-slate-400">Loading listings…</div>
        ) : filtered.length === 0 ? (
          <div className="mt-12 bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <PackageOpen className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-3">No items yet — be the first to sell something!</p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((l) => (
              <Link key={l.listing_id} to={`/marketplace/item/${l.listing_id}`}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md hover:border-slate-300 transition group"
                data-testid={`listing-card-${l.listing_id}`}>
                <div className="relative aspect-square bg-slate-100">
                  {l.photos?.[0] && <img src={l.photos[0]} alt={l.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />}
                  {l.video && (
                    <span className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"><PlayCircle className="h-4 w-4" /></span>
                  )}
                  {l.distance_km != null && (
                    <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 bg-white/90 text-slate-700 text-[11px] font-medium rounded-full px-2 py-0.5" data-testid={`listing-distance-${l.listing_id}`}>
                      <MapPin className="h-3 w-3 text-emerald-600" /> {l.distance_km} km
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <p className="font-medium text-slate-900 truncate">{l.title}</p>
                  <p className="text-emerald-700 font-semibold mt-0.5">{formatMoney(l.price, l.currency)}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{l.category} · {l.city}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
